import { useState } from "react";
import { api } from "./api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, ArrowUp, ArrowDown } from "lucide-react";
import { useResource, SectionHeader, Loading, Modal, ConfirmDialog, Field, PrimaryBtn, GhostBtn, inputCls } from "./components";
import { parseYouTubeId, youtubeThumb } from "@/utils/youtube";

const EMPTY = { url: "", title: "", description: "", featured: false };

export default function VideosSection() {
  const { items, loading, reload } = useResource("videos");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const sorted = [...items].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  const parsedId = editing ? parseYouTubeId(editing.url) : "";

  const save = async () => {
    if (!parsedId) {
      toast.error("That doesn't look like a YouTube link. Paste a link like youtube.com/watch?v=... or youtu.be/...");
      return;
    }
    if (!editing.title.trim()) {
      toast.error("Please enter a video title.");
      return;
    }
    setSaving(true);
    const payload = {
      url: editing.url.trim(),
      video_id: parsedId,
      title: editing.title.trim(),
      description: (editing.description || "").trim(),
      featured: !!editing.featured,
      order: editing.order ?? items.length,
    };
    try {
      if (editing.id) await api.put(`/admin/videos/${editing.id}`, payload);
      else await api.post("/admin/videos", payload);
      toast.success(editing.featured ? "Video saved and set as the Featured Video on the homepage!" : "Video saved! It's now in the gallery.");
      setEditing(null);
      reload();
    } catch {
      toast.error("Could not save the video. Please try again.");
    }
    setSaving(false);
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/videos/${deleting.id}`);
      toast.success("Video deleted.");
      setDeleting(null);
      reload();
    } catch {
      toast.error("Could not delete the video.");
    }
  };

  const toggleFeatured = async (v) => {
    try {
      await api.put(`/admin/videos/${v.id}`, { featured: !v.featured });
      toast.success(v.featured ? "Removed from homepage." : "Now the Featured Video on the homepage!");
      reload();
    } catch {
      toast.error("Could not update the video.");
    }
  };

  const move = async (index, dir) => {
    const list = [...sorted];
    const j = index + dir;
    if (j < 0 || j >= list.length) return;
    [list[index], list[j]] = [list[j], list[index]];
    try {
      await Promise.all(list.map((v, i) => api.put(`/admin/videos/${v.id}`, { order: i })));
      reload();
    } catch {
      toast.error("Could not reorder. Please try again.");
    }
  };

  if (loading) return <Loading />;

  return (
    <div data-testid="admin-videos-section">
      <SectionHeader
        title="Videos"
        subtitle="Paste a YouTube link, give it a title, and it appears in the gallery. Star one video to feature it on the homepage."
        action={
          <PrimaryBtn onClick={() => setEditing({ ...EMPTY })} data-testid="add-video-button">
            <Plus size={17} /> Add YouTube Video
          </PrimaryBtn>
        }
      />

      <div className="space-y-4" data-testid="videos-list">
        {sorted.length === 0 && (
          <p className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500">
            No videos yet. Click "Add YouTube Video" and paste a link.
          </p>
        )}
        {sorted.map((v, i) => (
          <div key={v.id} className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-4" data-testid={`video-row-${v.id}`}>
            <div className="relative shrink-0">
              <img src={youtubeThumb(v.video_id)} alt={v.title} className="h-16 w-28 rounded-md object-cover" />
              {v.featured && (
                <span className="absolute -top-2 -left-2 bg-[#D97706] text-white rounded-full p-1.5 shadow" title="Featured on homepage">
                  <Star size={12} className="fill-white" />
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-condensed text-lg font-bold uppercase text-slate-900 truncate">{v.title}</p>
              {v.description && <p className="text-xs text-slate-500 truncate">{v.description}</p>}
              <p className="text-[11px] text-slate-400 truncate mt-0.5">{v.url}</p>
            </div>
            <div className="flex flex-col gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded disabled:opacity-30" aria-label="Move up" data-testid={`video-up-${v.id}`}>
                <ArrowUp size={15} />
              </button>
              <button onClick={() => move(i, 1)} disabled={i === sorted.length - 1} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded disabled:opacity-30" aria-label="Move down" data-testid={`video-down-${v.id}`}>
                <ArrowDown size={15} />
              </button>
            </div>
            <button
              onClick={() => toggleFeatured(v)}
              className={`p-2.5 rounded-md ${v.featured ? "text-[#D97706] bg-[#D97706]/10" : "text-slate-400 hover:bg-slate-100"}`}
              aria-label="Toggle featured"
              title={v.featured ? "Remove from homepage" : "Feature on homepage"}
              data-testid={`video-feature-${v.id}`}
            >
              <Star size={17} className={v.featured ? "fill-[#D97706]" : ""} />
            </button>
            <button onClick={() => setEditing({ ...v })} className="p-2.5 text-[#0F4C2A] hover:bg-[#0F4C2A]/10 rounded-md" aria-label="Edit video" data-testid={`video-edit-${v.id}`}>
              <Pencil size={16} />
            </button>
            <button onClick={() => setDeleting(v)} className="p-2.5 text-red-700 hover:bg-red-50 rounded-md" aria-label="Delete video" data-testid={`video-delete-${v.id}`}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Video" : "Add YouTube Video"} onClose={() => setEditing(null)} testid="video-modal">
          <div className="space-y-4">
            <Field label="YouTube Link" hint="Paste the link — youtube.com/watch?v=... or youtu.be/... both work">
              <input className={inputCls} value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." data-testid="video-form-url" />
            </Field>
            {editing.url && (
              parsedId ? (
                <div className="rounded-md overflow-hidden border border-slate-200" data-testid="video-form-preview">
                  <img src={youtubeThumb(parsedId)} alt="Video thumbnail preview" className="w-full h-44 object-cover" />
                  <p className="text-xs font-semibold text-[#166534] bg-green-50 px-3 py-2">Video found — this is how it will look.</p>
                </div>
              ) : (
                <p className="text-xs font-semibold text-[#991B1B] bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="video-form-invalid">
                  We couldn't read that link. Paste a normal YouTube link.
                </p>
              )
            )}
            <Field label="Video Title">
              <input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="e.g. 2026 Season Highlights" data-testid="video-form-title" />
            </Field>
            <Field label="Short Description (optional)">
              <textarea rows={2} className={inputCls} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="video-form-description" />
            </Field>
            <label className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-md px-4 py-3 cursor-pointer">
              <input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="h-5 w-5 accent-[#0F4C2A]" data-testid="video-form-featured" />
              <span className="text-sm font-bold text-slate-700">Feature on the homepage <span className="font-normal text-slate-500">(replaces the current featured video)</span></span>
            </label>
            <div className="flex gap-3 pt-2">
              <PrimaryBtn className="flex-1" onClick={save} disabled={saving} data-testid="video-form-save">
                {saving ? "Saving..." : "Save Video"}
              </PrimaryBtn>
              <GhostBtn onClick={() => setEditing(null)} data-testid="video-form-cancel">Cancel</GhostBtn>
            </div>
          </div>
        </Modal>
      )}

      {deleting && (
        <ConfirmDialog
          message={`Delete the video "${deleting.title}"? This cannot be undone.`}
          onConfirm={remove}
          onCancel={() => setDeleting(null)}
          testid="video-delete-confirm"
        />
      )}
    </div>
  );
}
